import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { IAccount } from '@app/interfaces';
import { ApiFacadeService } from '@app/services/api-facade.service';
import { CacheService } from '@app/services/cache.service';
import { WrapToasterService } from '@app/services/wrap-toaster-service';
import { decompressGzip } from '@utils/helpers';

@Injectable({ providedIn: 'root' })
export class AdminAccountsService {
	constructor(
		private apiFacade: ApiFacadeService,
		private toasterService: WrapToasterService,
		private cache: CacheService,
	) {}

	/**
	 * Get accounts
	 * @param isCached - if true use FE service cache
	 * @param bypassBeCache - if 1 force BE to bypass their cache
	 */
	public getAdminAccounts(isCached: boolean, bypassBeCache: number): Observable<IAccount[]> {
		const cacheKey = 'admin-accounts';
		const cacheValue: IAccount[] | null = this.cache.get(cacheKey);

		if (cacheValue != null && isCached) {
			return of(cacheValue);
		}

		const url = `admin/accounts?bypass_cache=${+bypassBeCache}`;

		const observable = this.apiFacade.get<string>(url).pipe(
			map((res) => {
				const accounts = decompressGzip(res['accounts']) as IAccount[];

				this.cache.set(cacheKey, accounts);
				this.cache.set$(cacheKey, null);
				return accounts;
			}),
			catchError((error) => {
				console.log(error);
				this.toasterService.handleRequestFailed(error);
				return of([]);
			}),
		);

		this.cache.set$(cacheKey, observable);

		return observable;
	}

	public updateAccount(accountUuid: string, account: IAccount): Observable<any> {
		const payload = {
			account_uuid: accountUuid,
			attributes: {
				skip_marketplace: account.skip_marketplace,
				cv_standalone: account.cv_standalone,
				is_aispl: account.is_aispl,
				zd_enabled: account.zd_enabled,
			},
		};

		const url = `admin/accounts/${accountUuid}`;

		return this.apiFacade.put(url, payload).pipe(
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of(error);
			}),
		);
	}

	public deleteAccount(accountId: string, orgId: number): Observable<{ success: boolean }> {
		const cacheKey = 'admin-accounts';
		const url = `admin/accounts/${orgId}/${accountId}`;

		return this.apiFacade.delete<{ success: boolean }>(url).pipe(
			tap(() => {
				if (!this.cache.get(cacheKey)) {
					return;
				}
				const accounts = this.cache.get<IAccount[]>(cacheKey).filter((a) => a.id !== accountId);
				this.cache.set(cacheKey, accounts);
			}),
			map(() => ({ success: true })),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of({ success: false });
			}),
		);
	}

	public getAdminAccountPermissions(accountUuid: string): Observable<{ permissions: {} }> {
		const url = `admin/accounts/${accountUuid}/permissions`;

		return this.apiFacade.get(url).pipe(
			map((res) => ({ permissions: res['permissions'] })),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);
				return of({ permissions: {} });
			}),
		);
	}
}
